import { type FC, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import {
  Card,
  Spinner,
  Button,
  TextInput,
  Select,
  Alert,
} from "flowbite-react";
import NewContractModal from "../../components/contracts/NewContractModal";
import CustomerContractsTable from "../../components/contracts/CustomerContractsTable";
import { getContractTypesByCategory } from "../../services/contract-types";
import { resolveOrCreateAddressForRegeneration } from "../../services/addresses";
import { formatUtcDateTimeString } from "../../utils/dateFormatter";
import {
  clearNewContractModalOpenState,
  getNewContractModalDraft,
  getNewContractModalOpenState,
  isUpdateReloadSessionActive,
  saveNewContractModalOpenState,
} from "../../utils/updateReloadDraft";

const CONTRACT_STATUSES = [
  "Unterschrieben",
  "Warten auf QC",
  "Genehmigt",
  "Ausstehende Ausführung",
  "Aktiv",
  "Verlängerung ausstehend",
  "Storniert",
] as const;

type ContractStatus = typeof CONTRACT_STATUSES[number];
import { supabase, TABLES } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";

interface Address {
  id: number;
  street: string;
  house_number: string;
  zip_code: string;
  city: string;
  country: string;
}

interface Contract {
  id: number;
  contract_number: string;
  date_signed: string;
  status: string;
  contract_type: {
    id: number;
    type: string;
    company: string;
    category: string;
    description: string;
  };
}

interface ContractDetails {
  id: number;
  contract_number: string;
  date_signed: string;
  created_at: string;
  status: string;
  contract_type: Contract["contract_type"];
  customer: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    birthdate: string;
    notes: string;
    gender: number | null;
    address: Address;
  };
  contract_address: Address | null;
}

const CustomerInformationPage: FC = function () {
  const [contractDetails, setContractDetails] =
    useState<ContractDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [isEditingContract, setIsEditingContract] = useState(false);
  const [editedCustomer, setEditedCustomer] = useState<
    ContractDetails["customer"] | null
  >(null);
  const [editedContract, setEditedContract] = useState<Contract | null>(null);
  const [editedContractAddress, setEditedContractAddress] =
    useState<Address | null>(null);
  const [isSavingCustomer, setIsSavingCustomer] = useState(false);
  const [isSavingContract, setIsSavingContract] = useState(false);
  const [showNewContractModal, setShowNewContractModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [availableContractTypes, setAvailableContractTypes] = useState<
    Contract["contract_type"][]
  >([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const { secureToken } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [loadingContractTypes, setLoadingContractTypes] = useState(false);

  const toAddress = (address: any): Address => ({
    id: Number(address.id),
    street: address?.street || "",
    house_number: address?.house_number || "",
    zip_code: address?.zip_code || "",
    city: address?.city || "",
    country: address?.country || "",
  });

  const getDisplayedContractAddress = (details: ContractDetails): Address =>
    details.contract_address ?? details.customer.address;

  const isAddressComplete = (address: Address): boolean =>
    Boolean(
      address.street.trim() &&
        address.house_number.trim() &&
        address.zip_code.trim() &&
        address.city.trim()
    );

  const syncContractDetailsState = (details: ContractDetails) => {
    setContractDetails(details);
    setEditedCustomer(details.customer);
    setEditedContract({
      id: details.id,
      contract_number: details.contract_number,
      date_signed: details.date_signed,
      status: details.status,
      contract_type: details.contract_type,
    });
    setEditedContractAddress(getDisplayedContractAddress(details));
  };

  const fetchContractDetailsBySecureToken = async (): Promise<ContractDetails> => {
    if (!secureToken) {
      throw new Error("Vertrags-Token fehlt");
    }

    const { data, error } = await supabase
      .from(TABLES.CONTRACTS)
      .select(
        `
        id,
        contract_number,
        date_signed,
        created_at,
        status,
        contract_type_id,
        contract_address:address_id (
          id,
          street,
          house_number,
          zip_code,
          city,
          country
        ),
        contract_type:contract_type_id (
          id,
          type,
          company,
          category,
          description
        ),
        customer:customer_id (
          id,
          first_name,
          last_name,
          email,
          phone_number,
          birthdate,
          notes,
          gender,
          address:address_id (
            id,
            street,
            house_number,
            zip_code,
            city,
            country
          )
        )
      `
      )
      .eq("secure_token", secureToken)
      .single();

    if (error) throw error;
    if (!data) {
      throw new Error("Vertrag nicht gefunden");
    }

    const contractType = Array.isArray(data.contract_type)
      ? data.contract_type[0]
      : data.contract_type;
    const customer = Array.isArray(data.customer) ? data.customer[0] : data.customer;
    const customerAddressRaw = Array.isArray(customer?.address)
      ? customer?.address[0]
      : customer?.address;
    const contractAddressRaw = Array.isArray(data.contract_address)
      ? data.contract_address[0]
      : data.contract_address;

    if (!customer) {
      throw new Error("Kundendaten nicht gefunden");
    }
    if (!customerAddressRaw) {
      throw new Error("Adressdaten nicht gefunden");
    }
    if (!contractType) {
      throw new Error("Vertragstypendaten nicht gefunden");
    }

    return {
      id: data.id,
      contract_number: data.contract_number || "",
      date_signed: data.date_signed || "",
      created_at: data.created_at || "",
      status: data.status || "",
      contract_type: {
        id: contractType.id,
        type: contractType.type || "",
        company: contractType.company || "",
        category: contractType.category || "",
        description: contractType.description || "",
      },
      customer: {
        id: customer.id,
        first_name: customer.first_name || "",
        last_name: customer.last_name || "",
        email: customer.email || "",
        phone_number: customer.phone_number || "",
        birthdate: customer.birthdate || "",
        notes: customer.notes || "",
        gender: customer.gender || null,
        address: toAddress(customerAddressRaw),
      },
      contract_address: contractAddressRaw ? toAddress(contractAddressRaw) : null,
    };
  };

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!authLoading && !user) {
      navigate("/authentication/sign-in");
      return;
    }

    const fetchContractDetails = async () => {
      try {
        if (!user || !secureToken) return;
        const transformedData = await fetchContractDetailsBySecureToken();
        syncContractDetailsState(transformedData);
      } catch (err) {
        console.error("Error fetching contract details:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Fehler beim Laden der Vertragsdetails"
        );
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchContractDetails();
    }
  }, [user, authLoading, navigate, secureToken]);

  useEffect(() => {
    // Check if user is admin (role_id = 1)
    const checkUserRole = async () => {
      if (!user) return;

      try {
        const { data: workerData } = await supabase
          .from(TABLES.WORKERS)
          .select("role_id")
          .eq("user_id", user.id)
          .single();

        setIsAdmin(workerData?.role_id === 1);
      } catch (error) {
        console.error("Error checking user role:", error);
      }
    };

    if (!authLoading) {
      checkUserRole();
    }
  }, [user, authLoading]);

  useEffect(() => {
    // Fetch available contract types when editing is enabled
    const fetchContractTypes = async () => {
      try {
        setLoadingContractTypes(true);
        const types = await getContractTypesByCategory("all");
        setAvailableContractTypes(
          types.map((type) => ({
            id: type.id,
            type: type.type,
            company: type.company,
            category: type.category,
            description: type.description,
          }))
        );
      } catch (error) {
        console.error("Error fetching contract types:", error);
      } finally {
        setLoadingContractTypes(false);
      }
    };

    if (isEditingContract && isAdmin) {
      fetchContractTypes();
    }
  }, [isEditingContract, isAdmin]);

  useEffect(() => {
    if (!contractDetails || showNewContractModal) {
      return;
    }

    if (!isUpdateReloadSessionActive()) {
      return;
    }

    const draft = getNewContractModalDraft();
    const openState = getNewContractModalOpenState();
    const shouldReopen =
      (draft?.wasOpen && draft.customerId === contractDetails.customer.id) ||
      (openState?.wasOpen && openState.customerId === contractDetails.customer.id);

    if (shouldReopen) {
      clearNewContractModalOpenState();
      setShowNewContractModal(true);
    }
  }, [contractDetails, showNewContractModal]);

  useEffect(() => {
    if (!contractDetails || !showNewContractModal) {
      return;
    }

    saveNewContractModalOpenState(contractDetails.customer.id);
  }, [contractDetails, showNewContractModal]);

  const handleSaveCustomer = async () => {
    if (!editedCustomer) return;

    try {
      setIsSavingCustomer(true);
      setError(null);

      if (!isAddressComplete(editedCustomer.address)) {
        throw new Error(
          "Bitte Straße, Hausnummer, PLZ und Stadt vollständig eintragen"
        );
      }

      const resolvedCustomerAddress = await resolveOrCreateAddressForRegeneration(
        {
          street: editedCustomer.address.street,
          house_number: editedCustomer.address.house_number,
          zip_code: editedCustomer.address.zip_code,
          city: editedCustomer.address.city,
          country: editedCustomer.address.country,
          created_by_user_id: user?.id ?? null,
        }
      );

      const { error } = await supabase
        .from(TABLES.CUSTOMERS)
        .update({
          first_name: editedCustomer.first_name,
          last_name: editedCustomer.last_name,
          email: editedCustomer.email,
          phone_number: editedCustomer.phone_number,
          notes: editedCustomer.notes,
          address_id: resolvedCustomerAddress.id,
        })
        .eq("id", editedCustomer.id);

      if (error) throw error;

      const refreshed = await fetchContractDetailsBySecureToken();
      syncContractDetailsState(refreshed);
      setIsEditingCustomer(false);
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
    } catch (err) {
      console.error("Error updating customer:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Fehler beim Aktualisieren des Kunden"
      );
    } finally {
      setIsSavingCustomer(false);
    }
  };

  const handleSaveContract = async () => {
    if (!editedContract || !editedContractAddress || !contractDetails) return;

    try {
      setIsSavingContract(true);
      setError(null);

      if (!isAddressComplete(editedContractAddress)) {
        throw new Error(
          "Bitte Straße, Hausnummer, PLZ und Stadt vollständig eintragen"
        );
      }

      const resolvedContractAddress =
        await resolveOrCreateAddressForRegeneration({
          street: editedContractAddress.street,
          house_number: editedContractAddress.house_number,
          zip_code: editedContractAddress.zip_code,
          city: editedContractAddress.city,
          country: editedContractAddress.country,
          created_by_user_id: user?.id ?? null,
        });

      const { error: updateError } = await supabase
        .from(TABLES.CONTRACTS)
        .update({
          status: editedContract.status,
          contract_type_id: editedContract.contract_type.id,
          address_id: resolvedContractAddress.id,
        })
        .eq("id", contractDetails.id);

      if (updateError) throw updateError;

      const refreshed = await fetchContractDetailsBySecureToken();
      syncContractDetailsState(refreshed);

      setIsEditingContract(false);
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
    } catch (err) {
      console.error("Error updating contract:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Fehler beim Aktualisieren des Vertrags"
      );
    } finally {
      setIsSavingContract(false);
    }
  };

  if (loading || authLoading) {
    return (
      <NavbarSidebarLayout pageTitle="Kundeninformationen">
        <div className="flex h-full items-center justify-center">
          <Spinner size="xl" />
        </div>
      </NavbarSidebarLayout>
    );
  }

  if (error) {
    return (
      <NavbarSidebarLayout pageTitle="Kundeninformationen">
        <div className="p-4">
          {showSuccessAlert && (
            <Alert
              color="success"
              onDismiss={() => setShowSuccessAlert(false)}
              className="mb-4"
            >
              Vertrag erfolgreich gespeichert
            </Alert>
          )}
          <div className="rounded-lg bg-red-50 p-4 text-red-800 dark:bg-gray-800 dark:text-red-400">
            <p>Fehler beim Laden der Vertragsdetails: {error}</p>
          </div>
        </div>
      </NavbarSidebarLayout>
    );
  }

  if (!contractDetails) {
    return (
      <NavbarSidebarLayout pageTitle="Kundeninformationen">
        <div className="p-4">
          <div className="rounded-lg bg-yellow-50 p-4 text-yellow-800 dark:bg-gray-800 dark:text-yellow-400">
            <p>Keine Vertragsdetails gefunden</p>
          </div>
        </div>
      </NavbarSidebarLayout>
    );
  }

  return (
    <NavbarSidebarLayout pageTitle="Kundeninformationen">
      <div className="p-4">
        {showSuccessAlert && (
          <Alert
            color="success"
            onDismiss={() => setShowSuccessAlert(false)}
            className="mb-4"
          >
            Vertrag erfolgreich gespeichert
          </Alert>
        )}
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
              Vertragsdetails
            </h1>
            <Button onClick={() => setShowNewContractModal(true)}>
              Neuer Vertrag
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {/* Customer Contracts */}
          <div>
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Kundenverträge
            </h2>
            <CustomerContractsTable customerId={contractDetails.customer.id} />
          </div>

          {/* Customer and Contract Information */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Customer Information */}
            <Card>
              <div className="mb-4 flex items-center justify-between">
                <h5 className="text-xl font-bold text-gray-900 dark:text-white">
                  Kundeninformationen
                </h5>
                {!isEditingCustomer ? (
                  <Button size="sm" onClick={() => setIsEditingCustomer(true)}>
                    Bearbeiten
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      color="success"
                      onClick={handleSaveCustomer}
                      disabled={isSavingCustomer}
                    >
                      {isSavingCustomer ? "Wird gespeichert..." : "Speichern"}
                    </Button>
                    <Button
                      size="sm"
                      color="gray"
                      onClick={() => {
                        setIsEditingCustomer(false);
                        setEditedCustomer(contractDetails.customer ?? null);
                      }}
                      disabled={isSavingCustomer}
                    >
                      Abbrechen
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Name
                  </p>
                  {isEditingCustomer ? (
                    <div className="flex gap-2">
                      <TextInput
                        value={editedCustomer?.first_name}
                        onChange={(e) =>
                          setEditedCustomer((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  first_name: e.target.value,
                                }
                              : null
                          )
                        }
                        placeholder="Vorname"
                        className="dark:text-white"
                      />
                      <TextInput
                        value={editedCustomer?.last_name}
                        onChange={(e) =>
                          setEditedCustomer((prev) =>
                            prev ? { ...prev, last_name: e.target.value } : null
                          )
                        }
                        placeholder="Nachname"
                        className="dark:text-white"
                      />
                    </div>
                  ) : (
                    <p className="font-medium text-gray-900 dark:text-white">
                      {contractDetails.customer.first_name}{" "}
                      {contractDetails.customer.last_name}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Kontakt
                  </p>
                  {isEditingCustomer ? (
                    <div className="space-y-2">
                      <TextInput
                        value={editedCustomer?.email}
                        onChange={(e) =>
                          setEditedCustomer((prev) =>
                            prev ? { ...prev, email: e.target.value } : null
                          )
                        }
                        placeholder="E-Mail"
                        className="dark:text-white"
                      />
                      <TextInput
                        value={editedCustomer?.phone_number}
                        onChange={(e) =>
                          setEditedCustomer((prev) =>
                            prev
                              ? { ...prev, phone_number: e.target.value }
                              : null
                          )
                        }
                        placeholder="Telefonnummer"
                        className="dark:text-white"
                      />
                    </div>
                  ) : (
                    <>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {contractDetails.customer.email}
                      </p>
                      <p className="text-sm dark:text-gray-400">
                        {contractDetails.customer.phone_number}
                      </p>
                    </>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Kundenadresse
                  </p>
                  {isEditingCustomer ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <TextInput
                          value={editedCustomer?.address.street}
                          onChange={(e) =>
                            setEditedCustomer((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    address: {
                                      ...prev.address,
                                      street: e.target.value,
                                    },
                                  }
                                : null
                            )
                          }
                          placeholder="Straße"
                          className="flex-1 dark:text-white"
                        />
                        <TextInput
                          value={editedCustomer?.address.house_number}
                          onChange={(e) =>
                            setEditedCustomer((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    address: {
                                      ...prev.address,
                                      house_number: e.target.value,
                                    },
                                  }
                                : null
                            )
                          }
                          placeholder="Hausnummer"
                          className="w-32 dark:text-white"
                        />
                      </div>
                      <div className="flex gap-2">
                        <TextInput
                          value={editedCustomer?.address.zip_code}
                          onChange={(e) =>
                            setEditedCustomer((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    address: {
                                      ...prev.address,
                                      zip_code: e.target.value,
                                    },
                                  }
                                : null
                            )
                          }
                          placeholder="PLZ"
                          className="w-32 dark:text-white"
                        />
                        <TextInput
                          value={editedCustomer?.address.city}
                          onChange={(e) =>
                            setEditedCustomer((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    address: {
                                      ...prev.address,
                                      city: e.target.value,
                                    },
                                  }
                                : null
                            )
                          }
                          placeholder="Stadt"
                          className="flex-1 dark:text-white"
                        />
                      </div>
                      <TextInput
                        value={editedCustomer?.address.country}
                        onChange={(e) =>
                          setEditedCustomer((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    country: e.target.value,
                                  },
                                }
                              : null
                          )
                        }
                        placeholder="Land"
                        className="dark:text-white"
                      />
                    </div>
                  ) : (
                    <>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {contractDetails.customer.address.street}{" "}
                        {contractDetails.customer.address.house_number}
                      </p>
                      <p className="text-sm dark:text-gray-400">
                        {contractDetails.customer.address.zip_code}{" "}
                        {contractDetails.customer.address.city}
                      </p>
                      <p className="text-sm dark:text-gray-400">
                        {contractDetails.customer.address.country}
                      </p>
                    </>
                  )}
                </div>
                {contractDetails.customer.birthdate && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Geburtsdatum
                    </p>
                    <p className="font-medium dark:text-white">
                      {new Date(
                        contractDetails.customer.birthdate
                      ).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {contractDetails.customer.notes && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Notizen
                    </p>
                    {isEditingCustomer ? (
                      <TextInput
                        value={editedCustomer?.notes}
                        onChange={(e) =>
                          setEditedCustomer((prev) =>
                            prev ? { ...prev, notes: e.target.value } : null
                          )
                        }
                        placeholder="Notizen"
                        className="dark:text-white"
                      />
                    ) : (
                      <p className="font-medium dark:text-white">
                        {contractDetails.customer.notes}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Contract Information */}
            <Card>
              <div className="mb-4 flex items-center justify-between">
                <h5 className="text-xl font-bold text-gray-900 dark:text-white">
                  Vertragsinformationen
                </h5>
                {!isEditingContract ? (
                  <Button
                    size="sm"
                    onClick={() => {
                      setIsEditingContract(true);
                      setEditedContract({
                        id: contractDetails.id,
                        contract_number: contractDetails.contract_number,
                        date_signed: contractDetails.date_signed,
                        status: contractDetails.status,
                        contract_type: contractDetails.contract_type,
                      });
                      setEditedContractAddress(
                        getDisplayedContractAddress(contractDetails)
                      );
                    }}
                  >
                    Bearbeiten
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      color="success"
                      onClick={handleSaveContract}
                      disabled={isSavingContract}
                    >
                      {isSavingContract ? "Wird gespeichert..." : "Speichern"}
                    </Button>
                    <Button
                      size="sm"
                      color="gray"
                      onClick={() => {
                        setIsEditingContract(false);
                        setEditedContract({
                          id: contractDetails.id,
                          contract_number: contractDetails.contract_number,
                          date_signed: contractDetails.date_signed,
                          status: contractDetails.status,
                          contract_type: contractDetails.contract_type,
                        });
                        setEditedContractAddress(
                          getDisplayedContractAddress(contractDetails)
                        );
                      }}
                      disabled={isSavingContract}
                    >
                      Abbrechen
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Vertragsnummer
                  </p>
                  <p className="font-medium dark:text-white">
                    {contractDetails.contract_number}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Vertragstyp
                  </p>
                  {isEditingContract && isAdmin ? (
                    <div>
                      {loadingContractTypes ? (
                        <div className="flex items-center space-x-2">
                          <Spinner size="sm" />
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Lade Vertragstypen...
                          </span>
                        </div>
                      ) : (
                        <>
                          <Select
                            value={editedContract?.contract_type.id}
                            onChange={(e) => {
                              const selectedType = availableContractTypes.find(
                                (type) => type.id === Number(e.target.value)
                              );
                              if (selectedType && editedContract) {
                                setEditedContract({
                                  ...editedContract,
                                  contract_type: selectedType,
                                });
                              }
                            }}
                            disabled={isSavingContract}
                          >
                            {availableContractTypes.map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.type} - {type.company}
                              </option>
                            ))}
                          </Select>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {editedContract?.contract_type.category}
                          </p>
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="font-medium dark:text-white">
                        {contractDetails.contract_type.type}
                      </p>
                      <p className="text-sm dark:text-gray-400">
                        {contractDetails.contract_type.company}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {contractDetails.contract_type.category}
                      </p>
                    </>
                  )}
                </div>
                {contractDetails.contract_type.description && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Beschreibung
                    </p>
                    <p className="font-medium dark:text-white">
                      {contractDetails.contract_type.description}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Status
                  </p>
                  {isEditingContract ? (
                    <Select
                      value={editedContract?.status}
                      onChange={(e) =>
                        setEditedContract((prev) =>
                          prev
                            ? {
                                ...prev,
                                status: e.target.value as ContractStatus,
                              }
                            : null
                        )
                      }
                    >
                      {CONTRACT_STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <p className="font-medium dark:text-white">
                      {contractDetails.status}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Vertragsadresse
                  </p>
                  {!contractDetails.contract_address && (
                    <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">
                      Keine eigene Vertragsadresse gesetzt. Aktuell wird die
                      Kundenadresse verwendet.
                    </p>
                  )}
                  {isEditingContract ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <TextInput
                          value={editedContractAddress?.street || ""}
                          onChange={(e) =>
                            setEditedContractAddress((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    street: e.target.value,
                                  }
                                : null
                            )
                          }
                          placeholder="Straße"
                          className="flex-1 dark:text-white"
                        />
                        <TextInput
                          value={editedContractAddress?.house_number || ""}
                          onChange={(e) =>
                            setEditedContractAddress((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    house_number: e.target.value,
                                  }
                                : null
                            )
                          }
                          placeholder="Hausnummer"
                          className="w-32 dark:text-white"
                        />
                      </div>
                      <div className="flex gap-2">
                        <TextInput
                          value={editedContractAddress?.zip_code || ""}
                          onChange={(e) =>
                            setEditedContractAddress((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    zip_code: e.target.value,
                                  }
                                : null
                            )
                          }
                          placeholder="PLZ"
                          className="w-32 dark:text-white"
                        />
                        <TextInput
                          value={editedContractAddress?.city || ""}
                          onChange={(e) =>
                            setEditedContractAddress((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    city: e.target.value,
                                  }
                                : null
                            )
                          }
                          placeholder="Stadt"
                          className="flex-1 dark:text-white"
                        />
                      </div>
                      <TextInput
                        value={editedContractAddress?.country || ""}
                        onChange={(e) =>
                          setEditedContractAddress((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  country: e.target.value,
                                }
                              : null
                          )
                        }
                        placeholder="Land"
                        className="dark:text-white"
                      />
                    </div>
                  ) : (
                    <>
                      <p className="font-medium dark:text-white">
                        {getDisplayedContractAddress(contractDetails).street}{" "}
                        {getDisplayedContractAddress(contractDetails).house_number}
                      </p>
                      <p className="text-sm dark:text-gray-400">
                        {getDisplayedContractAddress(contractDetails).zip_code}{" "}
                        {getDisplayedContractAddress(contractDetails).city}
                      </p>
                      <p className="text-sm dark:text-gray-400">
                        {getDisplayedContractAddress(contractDetails).country}
                      </p>
                    </>
                  )}
                </div>
                {contractDetails.created_at && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Unterschrieben am
                    </p>
                    <p className="font-medium dark:text-white">
                      {formatUtcDateTimeString(contractDetails.created_at)}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
      {/* New Contract Modal */}
      <NewContractModal
        show={showNewContractModal}
        onClose={() => setShowNewContractModal(false)}
        onSuccess={() => {
          setShowNewContractModal(false);
          setShowSuccessAlert(true);
          setTimeout(() => setShowSuccessAlert(false), 3000);
        }}
        customer={contractDetails.customer}
      />
    </NavbarSidebarLayout>
  );
};

export default CustomerInformationPage;
